import { useAuthStore } from '../store/authStore.js'

export interface ApiError extends Error {
  status: number
  body: unknown
}

const STATE_MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

// Prevent concurrent refresh attempts across multiple failed requests
let refreshPromise: Promise<boolean> | null = null

/** Re-issues the session cookies; concurrent callers share one request. */
export async function doRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      })
      if (res.ok) {
        const data = await res.json() as { csrfToken?: string }
        const currentUser = useAuthStore.getState().user
        if (data.csrfToken && currentUser) {
          // Re-sync the in-memory csrfToken with the just-issued kc3.csrf
          // cookie. Without this, the next mutating request still sends the
          // stale token and 403s again.
          useAuthStore.getState().setSession({
            user: currentUser,
            csrfToken: data.csrfToken,
          })
        }
        return true
      }
      return false
    } catch {
      return false
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

async function request<T>(method: string, path: string, body?: unknown, customHeaders?: Record<string, string>, isRetry = false): Promise<T> {
  const { csrfToken } = useAuthStore.getState()
  const headers: Record<string, string> = customHeaders || { 'content-type': 'application/json' }
  if (STATE_MUTATING.has(method) && csrfToken) {
    headers['x-csrf-token'] = csrfToken
  }

  const init: RequestInit = { method, headers, credentials: 'include' }
  if (body !== undefined) {
    if (body instanceof FormData) {
      init.body = body
      delete headers['content-type']
    } else {
      init.body = JSON.stringify(body)
    }
  }
  const res = await fetch(`/api${path}`, init)

  const text = await res.text()
  const trimmed = text.trim()
  const parsed: unknown = trimmed ? JSON.parse(trimmed) : null

  // ── Silent session recovery ──
  // Two failure modes are recoverable without kicking the user back to the
  // login screen, and both are handled the same way: refresh the session
  // (which mints a fresh access cookie AND a fresh kc3.csrf cookie + updates
  // the in-memory csrfToken), then replay this one request.
  //
  //  - 401: the 2h access token expired.
  //  - 403 csrf_invalid: the kc3.csrf cookie and the in-memory csrfToken have
  //    drifted apart — typically after a server redeploy or a second login in
  //    another tab. `/auth/refresh` re-issues both in lockstep.
  //
  // `isRetry` guards against a loop: if the replayed request also fails auth,
  // we fall through to the normal error path (and the 401 branch below forces
  // logout).
  if (!isRetry && !path.startsWith('/auth/refresh')) {
    const isCsrfFailure =
      res.status === 403 &&
      parsed !== null &&
      typeof parsed === 'object' &&
      (parsed as { code?: string }).code === 'csrf_invalid'

    if (res.status === 401 || isCsrfFailure) {
      const refreshed = await doRefresh()
      if (refreshed) {
        return request<T>(method, path, body, customHeaders, true)
      }
    }
  }

  // A 2xx with an empty body is expected only for 204 No Content (DELETE
  // endpoints etc., which callers already type as void/null). Any other
  // 2xx with no body is an anomaly — most call sites immediately read a
  // property off the parsed response (e.g. `resp.messageId`), so silently
  // returning `null` here surfaces as a confusing downstream TypeError
  // instead of a clear network/server error at the actual fault point.
  if (res.ok && parsed === null && res.status !== 204) {
    const err = new Error(`${method} ${path} → ${res.status} with an empty response body`) as ApiError
    err.status = res.status
    err.body = null
    throw err
  }

  if (!res.ok) {
    // 401 here means the silent refresh above was not attempted (this IS the
    // retry, or the refresh call itself) or it failed — the session is truly
    // dead, so force logout. Platform tokens get 401 on tenant routes
    // ("Platform tokens are not accepted on tenant routes") even with a
    // valid session; force-logging out in that case causes a redirect loop
    // for platform admins, so we skip logout for that specific rejection.
    if (res.status === 401) {
      const isPlatformTokenRejection =
        parsed && typeof parsed === 'object' &&
        'message' in parsed &&
        typeof parsed.message === 'string' &&
        parsed.message.includes('Platform tokens are not accepted')
      if (!isPlatformTokenRejection) {
        if (parsed && typeof parsed === 'object' && 'message' in parsed && parsed.message === 'SESSION_REPLACED') {
          try { sessionStorage.setItem('kc3.logoutReason', 'replaced') } catch { /* private mode */ }
        }
        const { clearSession, user } = useAuthStore.getState()
        if (user) {
          console.warn('[API] 401 Unauthorized - forcing logout')
          clearSession()
          // Redirect to platform login if on a platform route, otherwise tenant login
          const isPlatformRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/platform')
          window.location.href = isPlatformRoute ? '/login/platform' : '/login'
        }
      }
    }

    // Try to extract error message from response body
    let errorMessage = `${method} ${path} → ${res.status}`
    if (parsed && typeof parsed === 'object') {
      if ('message' in parsed && typeof parsed.message === 'string') {
        errorMessage = parsed.message
      } else if ('error' in parsed && typeof parsed.error === 'string') {
        errorMessage = parsed.error
      }
      if ('issues' in parsed && Array.isArray(parsed.issues)) {
        errorMessage += ': ' + parsed.issues.map((i: { path?: unknown[]; message?: string }) => `${(i.path ?? []).join('.')}: ${i.message ?? ''}`).join(', ')
      }
    }

    const err = new Error(errorMessage) as ApiError
    err.status = res.status
    err.body = parsed
    throw err
  }
  return parsed as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown, customHeaders?: Record<string, string>) => request<T>('POST', path, body, customHeaders),
  put: <T>(path: string, body?: unknown, customHeaders?: Record<string, string>) => request<T>('PUT', path, body, customHeaders),
  patch: <T>(path: string, body?: unknown, customHeaders?: Record<string, string>) => request<T>('PATCH', path, body, customHeaders),
  delete: <T>(path: string) => request<T>('DELETE', path),
}

export const apiGet = <T>(path: string) => request<T>('GET', path)
export const apiPost = <T>(path: string, body?: unknown) => request<T>('POST', path, body)

/**
 * POST that hands back the raw `Response` so the caller can read a streaming
 * body (Server-Sent Events).
 *
 * `request()` cannot be used for this: it awaits `res.text()`, which blocks
 * until the stream closes — exactly the buffering a stream exists to avoid.
 * Credentials and the CSRF header are applied identically; only the response
 * handling differs. Non-2xx still throws, with the JSON error body attached,
 * so callers get the same failure shape as every other call.
 */
export async function apiPostStream(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  isRetry = false,
): Promise<Response> {
  const { csrfToken } = useAuthStore.getState()
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  if (csrfToken) headers['x-csrf-token'] = csrfToken

  const init: RequestInit = { method: 'POST', headers, credentials: 'include' }
  if (body !== undefined) init.body = JSON.stringify(body)
  if (signal) init.signal = signal

  const res = await fetch(`/api${path}`, init)

  // Same silent recovery as request(): a 401 or a 403 csrf_invalid on a
  // stream request is fixed by refreshing the session and replaying once.
  if (!isRetry && (res.status === 401 || res.status === 403)) {
    const peek = await res.clone().text().catch(() => '')
    const isCsrf = res.status === 403 && peek.includes('csrf_invalid')
    if (res.status === 401 || isCsrf) {
      const refreshed = await doRefresh()
      if (refreshed) return apiPostStream(path, body, signal, true)
    }
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    let parsed: unknown = null
    try {
      parsed = text.trim() ? JSON.parse(text) : null
    } catch {
      // Non-JSON error body (proxy HTML, plain text) — keep the raw text.
    }
    let message = `POST ${path} → ${res.status}`
    if (parsed && typeof parsed === 'object') {
      if ('message' in parsed && typeof parsed.message === 'string') message = parsed.message
      else if ('error' in parsed && typeof parsed.error === 'string') message = parsed.error
    } else if (text) {
      message = text.slice(0, 200)
    }
    const err = new Error(message) as ApiError
    err.status = res.status
    err.body = parsed ?? text
    throw err
  }

  return res
}
